import { useMemo, useState } from 'react';

import {
  useAllSessions,
  useCurrentUser,
  useIncrementalSessions,
  useView,
} from '@/hooks';
import { isAdmin, isCoach, SessionListType, SessionStatus } from '@/types';
import { Typography } from '@/ui';
import { groupByDate, track } from '@/utils';

import ClientCoaches from '../ClientCoaches';
import SessionList from '../List';
import SessionsTabs from '../Tabs';

import type { Session, SessionFeedback } from '@/types';
import type { FC } from 'react';

import Feedback from '@/components/Feedback';
import { buildAppointmentTimelineItems } from '@/features/appointments/AppointmentTimeline';
import { appointmentActionSession } from '@/features/appointments/appointmentActionSession';
import { useMemberScopeAppointmentRows } from '@/features/appointments/useMemberScopeAppointmentRows';
import { useGetAppointmentsQuery } from '@/store/api/appointmentApi';
import {
  useCreateFeedbackMutation,
  useUpdateFeedbackMutation,
} from '@/store/api/feedbackApi';

const appointmentSessionMonthKey = (session: Session): string => {
  const startedAt = new Date(session.startedAt);
  return `${startedAt.getMonth() + 1}_${startedAt.getFullYear()}`;
};

const shouldPreferNativeProjection = (
  legacySession: Session,
  nativeSession: Session,
): boolean => {
  const nativeVersion = nativeSession.sourceAppointmentVersion;
  if (nativeVersion === null || nativeVersion === undefined) return false;
  const legacyVersion = legacySession.sourceAppointmentVersion;
  return legacyVersion === null || legacyVersion === undefined
    ? true
    : nativeVersion > legacyVersion;
};

const appendDedupedSessions = (
  sessions: Record<string, Session[]>,
  nativeSessions: Session[],
): Record<string, Session[]> => {
  if (nativeSessions.length === 0) return sessions;
  const merged: Record<string, Session[]> = Object.fromEntries(
    Object.entries(sessions).map(([month, bucket]) => [month, [...bucket]]),
  );
  const representedAppointments = new Map<
    string,
    { month: string; session: Session }
  >();
  Object.entries(merged).forEach(([month, bucket]) => {
    bucket.forEach((session) => {
      const appointmentId = session.sourceAppointmentId ?? session.id;
      if (appointmentId)
        representedAppointments.set(appointmentId, { month, session });
    });
  });
  nativeSessions.forEach((session) => {
    const appointmentId = session.sourceAppointmentId ?? session.id;
    const represented = representedAppointments.get(appointmentId);
    if (represented) {
      if (!shouldPreferNativeProjection(represented.session, session)) return;
      const remainingLegacyRows = (merged[represented.month] ?? []).filter(
        (existingSession) => existingSession !== represented.session,
      );
      if (remainingLegacyRows.length === 0) {
        delete merged[represented.month];
      } else {
        merged[represented.month] = remainingLegacyRows;
      }
    }
    const month = appointmentSessionMonthKey(session);
    merged[month] = [...(merged[month] ?? []), session];
    representedAppointments.set(appointmentId, { month, session });
  });
  return merged;
};

const Past: FC = () => {
  const { user } = useCurrentUser();
  const { isCoach: isCoachPath, selectedUser } = useView();
  const concerningUserId = selectedUser.id || user.id;
  const [openFeedback, setOpenFeedback] = useState<Session | null>();

  const [createFeedback] = useCreateFeedbackMutation();
  const [updateFeedback] = useUpdateFeedbackMutation();

  // MEN-4928: no pagination control, so the list must be the whole history —
  // including a coach reading their own completed sessions on this page.
  const selfActingRole = isCoachPath ? 'coach' : 'member';
  const overviewActingRole =
    isCoach(user) && !isAdmin(user) ? 'coach' : undefined;
  const {
    data: sessions = {
      items: {} as Record<string, Session[]>,
      pages: 1,
      total: 1,
    },
    isLoading,
    refetch,
  } = useAllSessions({
    actingRole: selectedUser.id ? overviewActingRole : selfActingRole,
    order: 'desc',
    statuses: [SessionStatus.DONE],
    userId: concerningUserId,
  });

  // The redesigned date-led row from the Figma "Past Sessions" frame
  // (17420:41868) is for a member reading their own past sessions.
  //
  // Two guards:
  // - Not on the coach-viewing-a-member mount of this page, whose row links
  //   resolve relatively to /coach/members/:id/sessions/:id.
  // The pathname selects the self-view actor. Selected-member overview keeps
  // its existing role-based rendering because its counterparty is the selected
  // member, not the viewer.
  const isOwnMemberRoute =
    !selectedUser.id && !isCoachPath && concerningUserId === user.id;
  const memberRows = isOwnMemberRoute;
  // Member history is actor-authorized by Rails and must include retained
  // native appointments even after the current assignment has ended.
  const canFetchNativeAppointments = isOwnMemberRoute && Boolean(user.id);
  const nativeRangeEnd = useMemo(() => new Date().toISOString(), []);
  const {
    currentData: appointmentPage,
    isError: isAppointmentsError,
    isFetching: isAppointmentsFetching,
    isLoading: isAppointmentsLoading,
  } = useGetAppointmentsQuery(
    {
      actingRole: isOwnMemberRoute ? 'member' : undefined,
      bookingStatus: 'confirmed',
      lifecycleStatus: 'scheduled',
      rangeEnd: nativeRangeEnd,
      userId: concerningUserId,
    },
    { skip: !canFetchNativeAppointments },
  );

  const canFetchMemberScopeAppointments =
    Boolean(selectedUser.id) && (isAdmin(user) || isCoach(user));
  const memberScopeRowsReadOnly = !isCoach(user) || isAdmin(user);
  const memberScopeAppointmentRows = useMemberScopeAppointmentRows({
    coachId: memberScopeRowsReadOnly ? undefined : user.id,
    enabled: canFetchMemberScopeAppointments,
    memberId: selectedUser.id || '',
    readOnly: memberScopeRowsReadOnly,
    surface: 'past',
  });
  const canUseMemberScopeRows =
    canFetchMemberScopeAppointments &&
    !memberScopeAppointmentRows.isUnavailable &&
    (!memberScopeAppointmentRows.isError ||
      memberScopeAppointmentRows.adminSessions.length > 0);
  const memberScopeNativeIsDegraded =
    canFetchMemberScopeAppointments && memberScopeAppointmentRows.isError;

  const pastSessions = useMemo(() => {
    const nativePastAppointments = (appointmentPage?.appointments ?? []).filter(
      (appointment) =>
        appointment.bookingStatus === 'confirmed' &&
        appointment.lifecycleStatus === 'scheduled' &&
        Date.parse(appointment.currentSchedule.endAt) < Date.now(),
    );
    let merged = sessions.items;
    if (nativePastAppointments.length > 0) {
      merged = {};
      buildAppointmentTimelineItems(
        nativePastAppointments,
        Object.values(sessions.items).flat(),
      ).forEach((item) => {
        const session =
          item.kind === 'appointment'
            ? {
                ...appointmentActionSession(item.appointment),
                endedAt: item.appointment.currentSchedule.endAt,
                status: SessionStatus.DONE,
              }
            : item.session;
        const monthKey = appointmentSessionMonthKey(session);
        (merged[monthKey] ??= []).push(session);
      });
    }
    return groupByDate(
      appendDedupedSessions(
        merged,
        canUseMemberScopeRows ? memberScopeAppointmentRows.adminSessions : [],
      ),
      'desc',
    );
  }, [
    appointmentPage?.appointments,
    canUseMemberScopeRows,
    memberScopeAppointmentRows.adminSessions,
    sessions.items,
  ]);
  const hasPastSessions = Object.values(pastSessions).some(
    (bucket) => bucket.length > 0,
  );
  // The fetch is complete but the render is windowed: history grows without
  // bound for a coach, and every row here is a mounted card.
  const { sentinelRef, visibleSessions } = useIncrementalSessions(
    pastSessions,
    concerningUserId,
  );

  const handleOnFeedback = (session: Session) => {
    track('Clicked Provide Feedback', {
      sessionId: session.id,
    });
    setOpenFeedback({ ...session });
  };

  const onUpdate = async (feedback: SessionFeedback) => {
    try {
      if (feedback.id) {
        await updateFeedback({
          category: feedback.category,
          content: feedback.content,
          id: feedback.id,
        }).unwrap();
      } else {
        await createFeedback({
          category: feedback.category,
          content: feedback.content,
          sessionId: feedback.sessionId,
        }).unwrap();
      }
      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  const onClose = () => setOpenFeedback(null);

  return (
    <>
      {isOwnMemberRoute && (
        <ClientCoaches
          coaches={user.coaches}
          isLoading={isLoading}
          refetch={refetch}
          user={user}
        />
      )}
      <SessionsTabs />
      <SessionList
        hideIfEmpty={
          (canFetchNativeAppointments && isAppointmentsError) ||
          memberScopeNativeIsDegraded
        }
        loading={
          !hasPastSessions &&
          (isLoading ||
            (canFetchNativeAppointments &&
              !isAppointmentsError &&
              (isAppointmentsLoading || isAppointmentsFetching)) ||
            (canFetchMemberScopeAppointments &&
              memberScopeAppointmentRows.isLoading))
        }
        memberRows={memberRows}
        sessions={visibleSessions}
        type={SessionListType.Past}
        onFeedback={handleOnFeedback}
      />
      {canFetchNativeAppointments && isAppointmentsError && (
        <div
          className="border-marigold-300 bg-marigold-50 mt-4 rounded-xl border p-4"
          role="status"
        >
          <Typography className="text-cement-500" variant="body2">
            We couldn&apos;t load your Mento appointments right now. Any
            sessions above are shown from your existing schedule.
          </Typography>
        </div>
      )}
      <div
        ref={sentinelRef}
        data-testid="sessions-scroll-sentinel"
        aria-hidden
      />
      {memberScopeNativeIsDegraded && (
        <div
          className="border-marigold-300 bg-marigold-50 mt-4 rounded-xl border p-4"
          role="status"
        >
          <Typography className="text-cement-500" variant="body2">
            We couldn&apos;t load this member&apos;s Mento appointments right
            now. Any sessions above are shown from the member&apos;s existing
            history.
          </Typography>
        </div>
      )}
      {!!openFeedback && (
        <Feedback
          session={openFeedback}
          skipShowSubmitted
          onClose={onClose}
          onUpdate={onUpdate}
        />
      )}
    </>
  );
};

export default Past;
